import React, { useEffect, useState } from 'react';
import { auditAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Shield, RefreshCw, Activity } from 'lucide-react';

export default function AuditTrail() {
  const [items, setItems] = useState([]);
  const [mode, setMode] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadAudit = async () => {
    setLoading(true);
    setError('');
    try {
      const res = mode === 'security'
        ? await auditAPI.security(200)
        : await auditAPI.all(200);
      setItems(res.data.items || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load audit trail');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAudit();
  }, [mode]);

  const statusClass = (code) => {
    if (!code) return 'secondary';
    if (code >= 200 && code < 300) return 'default';
    if (code >= 400) return 'destructive';
    return 'secondary';
  };

  return (
    <div className="p-6 space-y-6" data-testid="audit-trail-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Shield className="w-8 h-8" />
            Audit Trail
          </h1>
          <p className="text-muted-foreground mt-1">
            Review admin activity, login attempts, failed access, and platform changes.
          </p>
        </div>

        <div className="flex gap-2">
          <Button variant={mode === 'all' ? 'default' : 'outline'} onClick={() => setMode('all')}>
            All Events
          </Button>
          <Button variant={mode === 'security' ? 'default' : 'outline'} onClick={() => setMode('security')}>
            Security
          </Button>
          <Button variant="outline" onClick={loadAudit} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Recent Events
          </CardTitle>
          <CardDescription>
            Showing latest {items.length} audit records.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 rounded-md bg-destructive/10 text-destructive p-3 text-sm">
              {error}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-3 px-3">Time</th>
                  <th className="py-3 px-3">Event</th>
                  <th className="py-3 px-3">Status</th>
                  <th className="py-3 px-3">Actor</th>
                  <th className="py-3 px-3">Path</th>
                  <th className="py-3 px-3">IP</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} className="border-b hover:bg-muted/50">
                    <td className="py-3 px-3 whitespace-nowrap">
                      {row.created_at ? new Date(row.created_at).toLocaleString() : '-'}
                    </td>
                    <td className="py-3 px-3 font-medium">{row.event}</td>
                    <td className="py-3 px-3">
                      <Badge variant={statusClass(row.status_code)}>
                        {row.status_code || '-'}
                      </Badge>
                    </td>
                    <td className="py-3 px-3 whitespace-nowrap">
                      {row.actor_email || 'System / unauthenticated'}
                      {row.actor_role && (
                        <div className="text-xs text-muted-foreground">{row.actor_role}</div>
                      )}
                    </td>
                    <td className="py-3 px-3">{row.path || '-'}</td>
                    <td className="py-3 px-3 whitespace-nowrap">{row.client_ip || '-'}</td>
                  </tr>
                ))}

                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan="6" className="py-8 text-center text-muted-foreground">
                      No audit records found.
                    </td>
                  </tr>
                )}

                {loading && (
                  <tr>
                    <td colSpan="6" className="py-8 text-center text-muted-foreground">
                      Loading audit trail...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
